import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import type { Stripe } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthContext } from "../../auth/AuthContext";
import LegalDocumentView from "../../components/legal/LegalDocumentView";
import {
  splitAtSubSection,
  useLegalDocument,
} from "../../components/legal/legalDocument";
import {
  CHECKOUT_SESSION_QUERY,
  ORDER_PREVIEW_QUERY,
} from "../../components/pricing-table/queries";
import {
  OrderPreview,
  OrderPreviewResponse,
} from "../../components/pricing-table/types";
import { checkoutOriginUrl, withEntryQuery } from "../../entryParams";
import { formatDate, formatMoneyCents, formatNumber } from "../../format";
import { createGraphQLClient, graphQLErrorCode } from "../../graphqlClient";
import {
  BILLING_COUNTRIES,
  countryLabel,
  detectBillingCountry,
} from "./countries";
import "./order-page.css";

/**
 * Stripe.js is fetched only once the checkout is shown. The plain package entry injects the
 * script on import, and the order page is bundled into every route - which would have Stripe set
 * its cookies on the cancellation and DSA pages too, where § 25 TDDDG does not cover them.
 */
let stripePromise: Promise<Stripe | null> | null = null;

const getStripe = () =>
  (stripePromise ??= loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!));

const WITHDRAWAL_DOCUMENT = "Widerrufsbelehrung_Lyra.md";
const IMPRINT_DOCUMENT = "Impressum_Lyra.md";
const WITHDRAWAL_FORM_HEADING = "Muster-Widerrufsformular";

type PreviewStatus =
  | "loading"
  | "ready"
  | "notFound"
  | "storeSubscription"
  | "alreadySubscribed"
  | "error";

/** The refusals of orderPreview and checkoutSessionToken (gg-367), as page states. */
const refusalStatus = (error: unknown): PreviewStatus => {
  switch (graphQLErrorCode(error)) {
    case "STORE_SUBSCRIPTION_ACTIVE":
      return "storeSubscription";
    case "SUBSCRIPTION_ALREADY_ACTIVE":
      return "alreadySubscribed";
    default:
      return "error";
  }
};

/**
 * Order page - the step between choosing a price and Stripe's embedded
 * checkout.
 *
 * It exists because Stripe's own button reads "Abonnieren" in subscription mode
 * and cannot be relabelled through the API. § 312j Abs. 2 and 3 BGB and AGB 14.2
 * require the essential characteristics, the storage quota, the total price
 * including the separately stated VAT amount, the billing interval, the term,
 * the cancellation conditions, the end of the trial, the date and amount of the
 * first payment and the reference to clause 19 to be shown directly above a
 * button labelled "zahlungspflichtig bestellen". Without that, no contract comes
 * about (§ 312j Abs. 4 BGB).
 *
 * All figures come from the backend query `orderPreview`; nothing is computed
 * here, least of all the tax amount.
 */
function OrderPage() {
  const params = useParams<{ pricingId: string }>();
  const pricingId = params.pricingId ? decodeURIComponent(params.pricingId) : "";
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { accessToken } = useAuthContext();

  const locale = i18n.language || "de";
  const [country, setCountry] = useState<string>(detectBillingCountry);
  const [preview, setPreview] = useState<OrderPreview | null>(null);
  const [status, setStatus] = useState<PreviewStatus>("loading");
  const [showCheckout, setShowCheckout] = useState(false);

  const withdrawal = useLegalDocument(WITHDRAWAL_DOCUMENT);
  const imprint = useLegalDocument(IMPRINT_DOCUMENT);

  const client = useMemo(
    () => createGraphQLClient(accessToken),
    [accessToken],
  );

  useEffect(() => {
    if (!pricingId) {
      setPreview(null);
      setStatus("notFound");
      return;
    }

    if (!accessToken) {
      setPreview(null);
      setStatus("error");
      return;
    }

    let cancelled = false;
    setStatus("loading");

    client
      .request<OrderPreviewResponse>(ORDER_PREVIEW_QUERY, {
        input: { pricingId, countryCode: country },
      })
      .then((data) => {
        if (cancelled) {
          return;
        }
        if (!data.orderPreview) {
          setPreview(null);
          setStatus("notFound");
          return;
        }
        setPreview(data.orderPreview);
        setStatus("ready");
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        setPreview(null);
        setStatus(refusalStatus(error));
      });

    return () => {
      cancelled = true;
    };
  }, [client, accessToken, pricingId, country]);

  const fetchClientSecret = useCallback(async (): Promise<string> => {
    try {
      const data = await client.request<{ checkoutSessionToken: string }>(
        CHECKOUT_SESSION_QUERY,
        {
          input: {
            originUrl: checkoutOriginUrl(),
            pricingId,
            // Same country the tax amount was disclosed for. The backend refuses the
            // session when it cannot establish that amount again.
            countryCode: country,
          },
        },
      );

      return data.checkoutSessionToken;
    } catch (error) {
      const refusal = refusalStatus(error);
      if (refusal !== "error") {
        setShowCheckout(false);
        setPreview(null);
        setStatus(refusal);
      }
      throw error;
    }
  }, [client, pricingId, country]);

  const checkoutOptions = useMemo(
    () => ({ fetchClientSecret }),
    [fetchClientSecret],
  );

  const goBack = () => navigate(withEntryQuery("/"));

  if (status === "loading") {
    return (
      <div className="order-page">
        <p className="order-loading">{t("order.loading")}</p>
      </div>
    );
  }

  if (status !== "ready" || !preview) {
    return (
      <div className="order-page">
        <div className="order-message order-message-error">
          <p>
            {status === "notFound"
              ? t("order.error.unknownPrice")
              : status === "storeSubscription"
                ? t("order.error.storeSubscription")
                : status === "alreadySubscribed"
                  ? t("order.error.alreadySubscribed")
                  : t("order.error.load")}
          </p>
          <button className="order-secondary-btn" onClick={goBack}>
            {t("order.back")}
          </button>
        </div>
      </div>
    );
  }

  const money = (cents: number | null | undefined): string | null =>
    cents === null || cents === undefined
      ? null
      : formatMoneyCents(cents, preview.currency, locale);

  // Interval, term and minimum term are mandatory disclosures. Anything other than
  // month or year - Stripe also knows day and week, and interval_count is not mapped
  // at all - must NOT silently read as "monthly": a wrong mandatory disclosure is
  // worse than none, so an unknown interval blocks the order like a missing tax
  // amount does.
  const intervalKnown =
    preview.interval === "year" || preview.interval === "month";

  const intervalLabel =
    preview.interval === "year"
      ? t("order.interval.year")
      : intervalKnown
        ? t("order.interval.month")
        : t("order.interval.unknown");

  const limitValue = (value: number): string =>
    value === -1 ? t("order.limits.unlimited") : formatNumber(value, locale);

  const storageValue = (): string => {
    if (preview.maxStorageMb === -1) {
      return t("order.limits.unlimited");
    }
    if (preview.maxStorageMb >= 1024) {
      return t("order.storage.gb", {
        size: formatNumber(preview.maxStorageMb / 1024, locale),
      });
    }
    return t("order.storage.mb", {
      size: formatNumber(preview.maxStorageMb, locale),
    });
  };

  const includedFeatures = [
    preview.supportsCollaboration ? t("order.features.collaboration") : null,
    preview.supportsAnalytics ? t("order.features.analytics") : null,
    preview.supportsPrioritySupport ? t("order.features.prioritySupport") : null,
  ].filter(Boolean) as string[];

  const termText =
    preview.interval === "year"
      ? t("order.term.year")
      : intervalKnown
        ? t("order.term.month")
        : t("order.term.unknown");

  const withdrawalParts = splitAtSubSection(
    withdrawal.blocks,
    WITHDRAWAL_FORM_HEADING,
  );

  const priceDisclosed = preview.priceFullyDisclosed;
  const withdrawalReady = withdrawal.status === "ready";
  const canOrder = priceDisclosed && withdrawalReady && intervalKnown;
  const showGermanOnlyHint = !locale.toLowerCase().startsWith("de");

  if (showCheckout) {
    return (
      <div className="order-page">
        <button className="order-back-link" onClick={() => setShowCheckout(false)}>
          {t("order.backToSummary")}
        </button>
        <h1 className="order-title">{t("order.checkout.title")}</h1>
        <p className="order-checkout-hint">{t("order.checkout.hint")}</p>
        <div className="order-checkout">
          <EmbeddedCheckoutProvider
            key={pricingId}
            stripe={getStripe()}
            options={checkoutOptions}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      </div>
    );
  }

  return (
    <div className="order-page">
      <button className="order-back-link" onClick={goBack}>
        {t("order.back")}
      </button>

      <h1 className="order-title">{t("order.title")}</h1>
      <p className="order-intro">
        {preview.trialAvailable
          ? t("order.intro.trial", { plan: preview.planName })
          : t("order.intro.subscription", { plan: preview.planName })}
      </p>

      {/*
        Mandatory information under AGB 14.2 / § 312j Abs. 2 BGB. This block sits
        directly above the order button and is neither collapsed nor merely
        linked.
      */}
      <section
        className="order-facts-panel"
        aria-label={t("order.facts.title")}
      >
        <h2 className="order-section-title">{t("order.facts.title")}</h2>

        <dl className="order-facts">
          <div className="order-fact">
            <dt>{t("order.facts.plan")}</dt>
            <dd>
              {preview.planName}
              {preview.planDescription ? (
                <span className="order-fact-note">
                  {preview.planDescription}
                </span>
              ) : null}
            </dd>
          </div>

          <div className="order-fact">
            <dt>{t("order.facts.scope")}</dt>
            <dd>
              <ul className="order-scope-list">
                <li>
                  {t("order.scope.projects", {
                    value: limitValue(preview.maxProjects),
                  })}
                </li>
                <li>
                  {t("order.scope.songs", {
                    value: limitValue(preview.maxSongsPerProject),
                  })}
                </li>
                <li>
                  {t("order.scope.setlists", {
                    value: limitValue(preview.maxSetListsPerProject),
                  })}
                </li>
                <li>
                  {t("order.scope.gigs", {
                    value: limitValue(preview.maxGigsPerProject),
                  })}
                </li>
                {includedFeatures.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            </dd>
          </div>

          {/* Storage quota - an essential characteristic, not just an AGB detail. */}
          <div className="order-fact order-fact-storage">
            <dt>{t("order.facts.storage")}</dt>
            <dd>{storageValue()}</dd>
          </div>

          <div className="order-fact order-fact-total">
            <dt>{t("order.facts.total")}</dt>
            <dd>
              {priceDisclosed ? (
                <>
                  {t("order.price.gross", {
                    amount: money(preview.grossCents),
                    interval: intervalLabel,
                  })}
                  <span className="order-fact-note">
                    {t("order.price.includesTax", {
                      amount: money(preview.taxCents),
                      taxType: t(`order.taxType.${preview.taxType}`, {
                        defaultValue: t("order.taxType.generic"),
                      }),
                    })}
                  </span>
                </>
              ) : (
                <span className="order-fact-unavailable">
                  {t("order.price.unavailable")}
                </span>
              )}
              <label className="order-country">
                <span className="order-country-label">
                  {t("order.country.label")}
                </span>
                <select
                  className="order-country-select"
                  value={country}
                  onChange={(event) => setCountry(event.target.value)}
                >
                  {BILLING_COUNTRIES.map((code) => (
                    <option key={code} value={code}>
                      {countryLabel(code, locale)}
                    </option>
                  ))}
                </select>
                <span className="order-country-label">
                  {t("order.country.hint")}
                </span>
              </label>
            </dd>
          </div>

          {preview.trialAvailable && preview.trialEndsAt ? (
            <div className="order-fact">
              <dt>{t("order.facts.trial")}</dt>
              <dd>
                {t("order.trial.value", {
                  count: preview.trialPeriodMonths,
                  date: formatDate(preview.trialEndsAt, locale),
                })}
                <span className="order-fact-note">
                  {t("order.trial.oncePerPaymentMethod")}
                </span>
              </dd>
            </div>
          ) : null}

          <div className="order-fact">
            <dt>{t("order.facts.firstPayment")}</dt>
            <dd>
              {priceDisclosed
                ? t("order.firstPayment.value", {
                    date: formatDate(preview.firstPaymentAt, locale),
                    amount: money(preview.firstPaymentGrossCents),
                  })
                : t("order.firstPayment.unavailable", {
                    date: formatDate(preview.firstPaymentAt, locale),
                  })}
            </dd>
          </div>

          <div className="order-fact">
            <dt>{t("order.facts.termAndCancellation")}</dt>
            <dd>
              {termText}
              <span className="order-fact-note">
                {t("order.cancellation.value")}
              </span>
            </dd>
          </div>

          <div className="order-fact">
            <dt>{t("order.facts.afterEnd")}</dt>
            <dd>{t("order.afterEnd.value")}</dd>
          </div>
        </dl>

        <ul className="order-notes">
          {preview.trialAvailable ? (
            <li>{t("order.notes.withdrawalPeriod")}</li>
          ) : (
            <>
              <li>{t("order.notes.trialAlreadyUsed")}</li>
              <li>{t("order.notes.withdrawalPeriodNoTrial")}</li>
            </>
          )}
          <li>{t("order.notes.paymentMethodsShort")}</li>
        </ul>
      </section>

      <section className="order-submit" aria-label={t("order.submit.aria")}>
        {!priceDisclosed ? (
          <p className="order-blocked">{t("order.blocked.price")}</p>
        ) : null}
        {!withdrawalReady && withdrawal.status !== "loading" ? (
          <p className="order-blocked">{t("order.blocked.withdrawal")}</p>
        ) : null}
        {!intervalKnown ? (
          <p className="order-blocked">{t("order.blocked.interval")}</p>
        ) : null}

        <button
          type="button"
          className="order-submit-btn"
          disabled={!canOrder}
          onClick={() => setShowCheckout(true)}
        >
          {t("order.submit.button")}
        </button>

        {/*
          The reference to the free trial goes below the button, never on it
          (AGB 14.2, the button label is prescribed by § 312j Abs. 3 BGB).
        */}
        <p className="order-submit-hint">
          {preview.trialAvailable
            ? t("order.submit.trialHint", {
                count: preview.trialPeriodMonths,
                amount: money(preview.grossCents),
              })
            : t("order.submit.subscriptionHint")}
        </p>
      </section>
      {/*
        Withdrawal instructions and model withdrawal form, visible before the
        order is placed (AGB 18, § 312d Abs. 1 BGB in conjunction with Art. 246a
        EGBGB). Collapsed, but part of this page - not a link somewhere else.
        No "immediate performance" checkbox: § 356 Abs. 5 BGB is deliberately not
        used, the 14 days stay intact.

        Placed BELOW the button on purpose. § 312j Abs. 2 BGB wants the mandatory
        disclosures "immediately before" the order is placed, which the courts read
        as spatial closeness to the button. An expanded instruction text between
        the price panel and the button would push exactly that apart - while being
        below it still satisfies "visible before the order is placed", because the
        customer cannot order without this page.
      */}
      <section className="order-legal" aria-label={t("order.withdrawal.title")}>
        <details className="order-details">
          <summary>{t("order.notes.title")}</summary>
          <ul className="order-notes order-notes-more">
            {preview.trialAvailable ? (
              <>
                <li>{t("order.notes.contractStartsWithTrial")}</li>
                <li>{t("order.notes.trialNeedsPaymentData")}</li>
              </>
            ) : null}
            <li>{t("order.notes.personalAccount")}</li>
            <li>{t("order.notes.contractPartner")}</li>
            <li>{t("order.notes.paymentMethods")}</li>
            <li>{t("order.notes.sepa")}</li>
            <li>{t("order.notes.failedPayment")}</li>
          </ul>
        </details>

        {withdrawal.status === "loading" ? (
          <p className="order-loading">{t("order.withdrawal.loading")}</p>
        ) : null}

        {withdrawal.status === "error" ? (
          <p className="order-message-error">{t("order.withdrawal.error")}</p>
        ) : null}

        {withdrawalReady ? (
          <>
            {showGermanOnlyHint ? (
              <p className="order-legal-hint">{t("order.legal.germanOnly")}</p>
            ) : null}

            <details className="order-details">
              <summary>{t("order.withdrawal.title")}</summary>
              <LegalDocumentView blocks={withdrawalParts.before} />
            </details>

            <details className="order-details">
              <summary>{t("order.withdrawal.formTitle")}</summary>
              <LegalDocumentView blocks={withdrawalParts.from} />
            </details>
          </>
        ) : null}

        {imprint.status === "ready" ? (
          <details className="order-details">
            <summary>{t("order.provider.title")}</summary>
            <LegalDocumentView blocks={imprint.blocks} />
          </details>
        ) : null}
      </section>

    </div>
  );
}

export default OrderPage;
